import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  DeleteGoodsReceiptPalletMutation,
  GoodsReceiptMutationDeletePalletArgs,
  GoodsReceiptPalletDeleteFailure,
} from "@/graphql/codegen/graphql"
import { DeleteGoodsReceiptPalletDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseDeleteGoodsReceiptPalletParams {
  onSuccess?: () => void
}

interface UseDeleteGoodsReceiptPalletReturn {
  deletePallet: (palletId: string) => Promise<void>
  isDeleting: boolean
  error?: CombinedError
}

export const useDeleteGoodsReceiptPallet = ({
  onSuccess,
}: UseDeleteGoodsReceiptPalletParams = {}): UseDeleteGoodsReceiptPalletReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    DeleteGoodsReceiptPalletMutation,
    GoodsReceiptMutationDeletePalletArgs
  >(DeleteGoodsReceiptPalletDocument)

  const deletePallet = async (palletId: string): Promise<void> => {
    const { data, error } = await executeMutation({
      input: { goodsReceiptPalletId: palletId },
    })
    const output = data?.goodsReceipt.deletePallet
    const hasError = error || output?.__typename === "GoodsReceiptPalletDeleteFailure"

    if (hasError) {
      const errorMessage =
        error?.message ?? (output as GoodsReceiptPalletDeleteFailure).error ?? "Failed to delete pallet"
      notify({
        type: NotificationType.Error,
        message: errorMessage,
      })
      return
    }

    notify({
      type: NotificationType.Success,
      content: "Pallet deleted successfully",
    })
    onSuccess?.()
  }

  return {
    deletePallet,
    isDeleting: fetching,
    error,
  }
}
